// src/pages/Orders.js
import React, { useEffect, useState, useCallback } from 'react';
import { getOrders, listProducts, createOrder } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { T, fmt, Badge, Card, Btn, Empty, Spinner, SectionTitle, TH, TD, INP, LBL, InfoBox } from '../components/UI';

export default function Orders() {
  const { can } = useAuth();
  const [orders, setOrders]     = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showNew, setShowNew]   = useState(false);
  const [filter, setFilter]     = useState('ALL');
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  const [form, setForm] = useState({
    customer_name: '', customer_contact: '', customer_email: '',
    delivery_cost: '', notes: '',
    items: [{ product_id: '', quantity: 1 }]
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, pRes] = await Promise.all([getOrders(), listProducts()]);
      setOrders(oRes.data.data);
      setProducts(pRes.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { product_id: '', quantity: 1 }] }));
  const removeItem = i => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, key, val) => setForm(f => {
    const items = [...f.items];
    items[i] = { ...items[i], [key]: val };
    return { ...f, items };
  });

  // Price comes from the product in the price list — staff cannot set/override it
  const getItemPrice = (item) => {
    const p = products.find(p => String(p.id) === String(item.product_id));
    return p ? Number(p.price) : 0;
  };

  const itemsTotal = form.items.reduce((s, i) => s + (Number(i.quantity)||0) * getItemPrice(i), 0);
  const deliveryCost = Number(form.delivery_cost) || 0;
  const total = itemsTotal + deliveryCost;

  const handleCreate = async () => {
    if (!form.customer_name.trim()) return setErr('Customer name is required.');
    if (form.items.some(i => !i.product_id)) return setErr('Select a product from the price list for each item.');
    if (form.items.some(i => !i.quantity || Number(i.quantity) <= 0)) return setErr('All items need a valid quantity.');
    setSaving(true); setErr('');
    try {
      await createOrder({
        customer_name: form.customer_name,
        customer_contact: form.customer_contact,
        customer_email: form.customer_email,
        delivery_cost: deliveryCost,
        notes: form.notes,
        items: form.items.map(i => ({ product_id: Number(i.product_id), quantity: Number(i.quantity) }))
      });
      setShowNew(false);
      setForm({ customer_name:'', customer_contact:'', customer_email:'', delivery_cost:'', notes:'', items:[{ product_id:'', quantity:1 }] });
      await load();
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to create order.');
    } finally { setSaving(false); }
  };

  const statuses = ['ALL', ...new Set(orders.map(o => o.status))];
  const filtered = filter === 'ALL' ? orders : orders.filter(o => o.status === filter);

  if (loading) return <Spinner />;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
        <SectionTitle sub="Invoice auto-generated on every order creation">Order Management</SectionTitle>
        {can('create_order') && <Btn onClick={() => setShowNew(true)}>+ New Order</Btn>}
      </div>


      {/* New Order Modal */}
      {showNew && (
        <div style={{ position:'fixed', inset:0, background:'#020812CC', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:'20px' }}>
          <div style={{ background:T.card, border:`1px solid ${T.gold}44`, borderRadius:'14px', padding:'28px', width:'100%', maxWidth:'580px', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ fontSize:'16px', fontWeight:700, color:T.gold, marginBottom:'20px' }}>➕ New Order</div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'12px' }}>
              <div>
                <label style={LBL}>Customer Name *</label>
                <input style={INP} value={form.customer_name} onChange={e => setForm(f => ({...f, customer_name:e.target.value}))} placeholder="e.g. Nairobi Events Ltd" />
              </div>
              <div>
                <label style={LBL}>Contact</label>
                <input style={INP} value={form.customer_contact} onChange={e => setForm(f => ({...f, customer_contact:e.target.value}))} placeholder="+254 700 000 000" />
              </div>
            </div>
            <div style={{ marginBottom:'14px' }}>
              <label style={LBL}>Customer Email</label>
              <input style={INP} value={form.customer_email} onChange={e => setForm(f => ({...f, customer_email:e.target.value}))} placeholder="email@example.co.ke" />
            </div>

            {/* Items */}
            <div style={{ marginBottom:'14px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                <label style={LBL}>Order Items *</label>
                <Btn size="sm" variant="outline" onClick={addItem}>+ Add Item</Btn>
              </div>
              {products.length === 0 && (
                <div style={{ fontSize:'12px', color:T.warning, marginBottom:'8px', padding:'10px', background:`${T.warning}11`, borderRadius:'8px', border:`1px solid ${T.warning}33` }}>
                  ⚠ No products in the price list yet. Ask management to add products before creating an order.
                </div>
              )}
              {form.items.map((item, i) => {
                const p = products.find(p => String(p.id) === String(item.product_id));
                return (
                  <div key={i} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:'8px', padding:'12px', marginBottom:'8px' }}>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 80px auto', gap:'8px', alignItems:'flex-start' }}>
                      <div>
                        <label style={{ ...LBL, marginBottom:'4px' }}>Product</label>
                        <select value={item.product_id} onChange={e => updateItem(i,'product_id',e.target.value)}
                          style={{ ...INP, marginBottom:0 }}>
                          <option value="">— Select from price list —</option>
                          {products.map(p => <option key={p.id} value={p.id}>{p.name} — KES {Number(p.price).toLocaleString()} per {p.unit}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ ...LBL, marginBottom:'4px' }}>Qty</label>
                        <input style={{ ...INP, marginBottom:0 }} type="number" min="1" value={item.quantity} onChange={e => updateItem(i,'quantity',e.target.value)} />
                      </div>
                      {form.items.length > 1 && (
                        <div style={{ paddingTop:'22px' }}>
                          <button onClick={() => removeItem(i)} style={{ background:'none', border:'none', color:T.danger, cursor:'pointer', fontSize:'20px', lineHeight:1 }}>×</button>
                        </div>
                      )}
                    </div>
                    {p && item.quantity > 0 && (
                      <div style={{ fontSize:'11px', color:T.muted, marginTop:'8px', display:'flex', justifyContent:'space-between' }}>
                        <span>Unit price: <b style={{ color:T.gold }}>KES {Number(p.price).toLocaleString()}</b></span>
                        <span>Subtotal: <b style={{ color:T.gold }}>KES {(Number(item.quantity) * Number(p.price)).toLocaleString()}</b></span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Delivery cost */}
            <div style={{ marginBottom:'14px' }}>
              <label style={LBL}>Delivery Cost (KES) — optional</label>
              <input style={INP} type="number" min="0" step="0.01" value={form.delivery_cost} onChange={e => setForm(f => ({...f, delivery_cost: e.target.value}))} placeholder="0 if no delivery charge" />
            </div>

            <div style={{ marginBottom:'14px' }}>
              <label style={LBL}>Notes</label>
              <input style={INP} value={form.notes} onChange={e => setForm(f => ({...f, notes:e.target.value}))} placeholder="Special instructions..." />
            </div>

            {total > 0 && (
              <div style={{ background:`${T.gold}11`, border:`1px solid ${T.gold}33`, borderRadius:'8px', padding:'12px 16px', marginBottom:'14px' }}>
                {deliveryCost > 0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'13px', color:T.muted, marginBottom:'6px' }}>
                    <span>Items subtotal</span><span>KES {itemsTotal.toLocaleString()}</span>
                  </div>
                )}
                {deliveryCost > 0 && (
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:'13px', color:T.muted, marginBottom:'8px', paddingBottom:'8px', borderBottom:`1px solid ${T.border}` }}>
                    <span>Delivery</span><span>KES {deliveryCost.toLocaleString()}</span>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontWeight:700, color:T.text }}>Order Total</span>
                  <span style={{ fontWeight:800, color:T.gold, fontSize:'16px' }}>KES {total.toLocaleString()}</span>
                </div>
              </div>
            )}

            <div style={{ background:`${T.info}11`, border:`1px solid ${T.info}33`, borderRadius:'6px', padding:'8px 12px', marginBottom:'14px', fontSize:'12px', color:T.info }}>
              ✦ Auto: Order ID (MS-XXXXXX) · Invoice (INV-XXXXXX) · Workflow trigger
            </div>

            {err && <div style={{ color:T.danger, fontSize:'12px', marginBottom:'10px' }}>{err}</div>}

            <div style={{ display:'flex', gap:'10px' }}>
              <Btn onClick={handleCreate} disabled={saving} style={{ flex:1, padding:'11px' }}>{saving ? 'Creating...' : 'Create Order'}</Btn>
              <Btn variant="outline" onClick={() => { setShowNew(false); setErr(''); }} style={{ flex:1, padding:'11px' }}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:'6px', marginBottom:'14px', flexWrap:'wrap' }}>
        {statuses.map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ background:filter===s?T.gold:'transparent', color:filter===s?'#000':T.muted, border:`1px solid ${filter===s?T.gold:T.border}`, borderRadius:'6px', padding:'4px 12px', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
            {s.replace(/_/g,' ')}
          </button>
        ))}
      </div>

      <Card>
        {filtered.length === 0
          ? <Empty msg={orders.length === 0 ? 'No orders yet — create the first one!' : 'No orders match this filter.'} />
          : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr>
                <th style={TH}>Order ID</th><th style={TH}>Customer</th><th style={TH}>Amount</th><th style={TH}>Status</th><th style={TH}>Invoice</th><th style={TH}>Created By</th><th style={TH}>Date</th>
              </tr></thead>
              <tbody>
                {filtered.map(o => (
                  <tr key={o.id}>
                    <td style={{ ...TD, color:T.gold, fontWeight:700 }}>{o.order_number}</td>
                    <td style={TD}>{o.customer_name}</td>
                    <td style={{ ...TD, fontWeight:600 }}>{fmt(o.total_amount)}</td>
                    <td style={TD}><Badge status={o.status} /></td>
                    <td style={{ ...TD, fontSize:'12px', color:o.invoice_number?T.info:T.muted }}>{o.invoice_number||'—'}</td>
                    <td style={{ ...TD, fontSize:'12px', color:T.muted }}>{o.created_by_name}</td>
                    <td style={{ ...TD, fontSize:'12px', color:T.muted }}>{new Date(o.created_at).toLocaleDateString('en-KE')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Card>
    </div>
  );
}
